import React, { useState, useEffect } from "react";
import { Skeleton } from "@mui/material";
import { RootStateOrAny, useSelector } from "react-redux";
import { AuthState } from "store/ducks/auth/types";
import { CARGO_CODES } from "constants/permissions";
import DatePickerRangeG from "components/DatePickerRangeG";
import { useListarUsuariosCorretores } from "services/api/Identity_Usuario/listar-usuarios-corretores";
import { useListarUsuariosPorEquipe } from "services/api/Identity_Usuario/listar-usuarios-por-equipe";
import {
  useListarSuperintendentes,
  useListarEquipes,
  useListarCorretores as useListarCorretoresPorEquipeCheckin,
} from "services/api/AppWeb_Checkin/listar_super_equipe_corretor";
import axiosLead from "services/lead";
import { useListarTiposAtendimentos } from "services/api/AppWeb_TipoAtendimento";
import * as S from "./styles";
import * as IconsSVG from "../../../../assets/iconsSVG";

interface Option {
  idEquipeLiderUsuario?: string; // Para equipes (líderes) - usado para filtro de API
  value: string;
  label: string;
  idEquipe?: string;
}

interface FiltroVisitasProps {
  onClose: () => void;
  onApplyFilters: (filters: any) => void;
  onClearFilters: () => void;
  appliedFilters: any | null;
  isOpen: boolean;
}

const FiltroVisitas: React.FC<FiltroVisitasProps> = ({
  onClose,
  onApplyFilters,
  onClearFilters,
  appliedFilters,
  isOpen,
}) => {
  const { codigoCargo2013, userId, usuarioDiretor } = useSelector<
    RootStateOrAny,
    AuthState
  >((state) => state.user);

  // Estados dos filtros
  const [dataVisitaInicio, setDataVisitaInicio] = useState<Date | null>(null);
  const [dataVisitaFim, setDataVisitaFim] = useState<Date | null>(null);
  const [origem, setOrigem] = useState<Option[]>([]);
  const [canalContato, setCanalContato] = useState<Option[]>([]);
  const [tipoAtendimento, setTipoAtendimento] = useState<Option[]>([]);
  const [empreendimento, setEmpreendimento] = useState<Option[]>([]);
  const [corretorAtendimento, setCorretorAtendimento] = useState<Option[]>([]);
  const [corretorOrigem, setCorretorOrigem] = useState<Option[]>([]);
  const [equipe, setEquipe] = useState<Option[]>([]);
  const [superintendente, setSuperintendente] = useState<Option[]>([]);

  // Listas auxiliares
  const [origensList, setOrigensList] = useState<Option[]>([]);
  const [canaisContatoList, setCanaisContatoList] = useState<Option[]>([]);
  const [loadingCanais, setLoadingCanais] = useState(false);
  const [empreendimentosList, setEmpreendimentosList] = useState<Option[]>([]);
  const [loadingEmpreendimentos, setLoadingEmpreendimentos] = useState(false);

  // Tipos de atendimento via API
  const { data: dataTiposAtendimento } = useListarTiposAtendimentos();

  const tiposAtendimentoList: Option[] =
    dataTiposAtendimento?.data?.map((item: any) => ({
      value: String(item.id),
      label: item.nome,
    })) || [];

  const isCorretor = codigoCargo2013 === CARGO_CODES.CORRETOR;
  const isGerente =
    codigoCargo2013 === CARGO_CODES.GERENTE_VENDAS ||
    codigoCargo2013 === CARGO_CODES.GERENTE_COMERCIAL;
  const isDiretor = usuarioDiretor === true;
  const isSuperintendente =
    codigoCargo2013 === CARGO_CODES.SUPERINTENDENTE && !isDiretor;

  // Corretores via API
  const { data: dataCorretores, refetch: refetchCorretores } =
    useListarUsuariosCorretores({
      queryKey: "filtro-visitas-corretores",
    });

  // Equipes (líderes) via API — usado para superintendente
  const { data: dataEquipes, refetch: refetchEquipes } =
    useListarUsuariosPorEquipe({
      queryKey: "filtro-visitas-equipes",
    });

  // Corretores por equipe — queryKey dinâmica + enabled dinâmico para disparar automaticamente ao mudar equipe
  const { data: dataCorretoresPorEquipe } = useListarCorretoresPorEquipeCheckin(
    {
      queryKey: `filtro-visitas-corretores-por-equipe-${equipe
        .map((e) => e.value)
        .join(",")}`,
      IdsEquipes: equipe.map((e) => e.value),
      enabled: isOpen && equipe.length > 0,
    }
  );

  // Superintendentes via API — usado para diretor
  const { data: dataSuperintendentes } = useListarSuperintendentes({
    queryKey: "filtro-visitas-superintendentes",
    userId,
    enabled: isOpen && isDiretor,
  });

  // Equipes filtradas por superintendente selecionado — usado para diretor
  const { data: dataEquipesPorSuperintendente } = useListarEquipes({
    queryKey: `filtro-visitas-equipes-por-super-${superintendente
      .map((s) => s.value)
      .join(",")}`,
    IdsSuperintendentes: superintendente.map((s) => s.value),
    enabled: isOpen && isDiretor && superintendente.length > 0,
  });

  // Buscar dados ao abrir o filtro
  useEffect(() => {
    if (!isOpen) return;
    refetchCorretores();
    if (isSuperintendente) refetchEquipes();
  }, [isOpen]);

  // Quando superintendente muda (diretor), limpar equipe e corretor de origem
  useEffect(() => {
    if (isDiretor) {
      setEquipe([]);
      setCorretorOrigem([]);
    }
  }, [superintendente]);

  // Quando equipe muda, limpar seleção de corretor de origem
  // (a busca de corretores é feita automaticamente pelo hook com enabled dinâmico)
  useEffect(() => {
    setCorretorOrigem([]);
  }, [equipe]);

  // Carregar origens
  useEffect(() => {
    const loadOrigens = async () => {
      try {
        const response = await axiosLead.GetValidOrigin(true);
        setOrigensList(
          response.data.data.map((item: any) => ({
            value: item.id,
            label: item.nome,
          }))
        );
      } catch (error) {
        console.error("Erro ao carregar origens:", error);
      }
    };
    loadOrigens();
  }, []);

  // Carregar empreendimentos via axiosLead.GetEnterprises (mesmo de Produto de Interesse)
  useEffect(() => {
    const loadEmpreendimentos = async () => {
      setLoadingEmpreendimentos(true);
      try {
        const response = await axiosLead.GetEnterprises();
        setEmpreendimentosList(
          response.data.data.map((item: any) => ({
            value: item.id,
            label: item.nome,
          }))
        );
      } catch (error) {
        console.error("Erro ao carregar empreendimentos:", error);
      } finally {
        setLoadingEmpreendimentos(false);
      }
    };
    loadEmpreendimentos();
  }, []);

  // Carregar canais quando origens mudam
  useEffect(() => {
    const loadCanais = async () => {
      setCanalContato([]);
      if (origem.length === 0) {
        setCanaisContatoList([]);
        return;
      }
      setLoadingCanais(true);
      try {
        const idsOrigem = origem.map((o) => o.value);
        const response = await axiosLead.GetCanaisContato(idsOrigem);
        setCanaisContatoList(
          response.data.data.map((item: any) => ({
            value: item.id,
            label: item.nome,
          }))
        );
      } catch (error) {
        console.error("Erro ao carregar canais de contato:", error);
        setCanaisContatoList([]);
      } finally {
        setLoadingCanais(false);
      }
    };
    loadCanais();
  }, [origem]);

  // Resetar filtros quando o drawer abrir sem filtros aplicados
  useEffect(() => {
    if (isOpen && !appliedFilters) {
      setDataVisitaInicio(null);
      setDataVisitaFim(null);
      setOrigem([]);
      setCanalContato([]);
      setTipoAtendimento([]);
      setEmpreendimento([]);
      setCorretorAtendimento([]);
      setCorretorOrigem([]);
      setEquipe([]);
      setSuperintendente([]);
    }
  }, [isOpen, appliedFilters]);

  const handleStartDateChange = (newDate: Date | null) => {
    setDataVisitaInicio(newDate);
    if (newDate && dataVisitaFim && newDate > dataVisitaFim) {
      setDataVisitaFim(null);
    }
  };

  const handleEndDateChange = (newDate: Date | null) => {
    setDataVisitaFim(newDate);
    if (newDate && dataVisitaInicio && newDate < dataVisitaInicio) {
      setDataVisitaInicio(null);
    }
  };

  const handleClearFilters = () => {
    setDataVisitaInicio(null);
    setDataVisitaFim(null);
    setOrigem([]);
    setCanalContato([]);
    setTipoAtendimento([]);
    setEmpreendimento([]);
    setCorretorAtendimento([]);
    setCorretorOrigem([]);
    setEquipe([]);
    setSuperintendente([]);
    onClearFilters();
  };

  const handleApplyFilters = () => {
    onApplyFilters({
      dataVisitaInicio,
      dataVisitaFim,
      origem: origem.map((o) => o.value),
      canalContato: canalContato.map((c) => c.value),
      tipoAtendimento: tipoAtendimento.map((t) => t.value),
      empreendimento: empreendimento.map((e) => e.value),
      corretorAtendimento: corretorAtendimento.map((c) => c.value),
      corretorOrigem: corretorOrigem.map((c) => c.value),
      equipe: equipe.map((e) => e.idEquipeLiderUsuario || e.value),
      superintendente: superintendente.map((s) => s.idEquipe || s.value),
    });
    onClose();
  };

  const corretoresOptions: Option[] =
    dataCorretores?.data?.map((c: any) => ({
      value: c.idCorretor ?? c.id,
      label: c.nome || c.apelido || "",
    })) || [];

  // Para superintendente/diretor com equipe selecionada, usar corretores filtrados pela equipe
  const corretoresOrigemOptions: Option[] =
    (isSuperintendente || isDiretor) && equipe.length > 0
      ? dataCorretoresPorEquipe?.data?.map((c: any) => ({
          value: c.idCorretor ?? c.id,
          label: c.apelido || c.nome || "",
        })) || []
      : corretoresOptions;

  // Equipes para superintendente (busca direta) ou diretor (filtrada por superintendente selecionado)
  // value = idUsuario (para cascata de busca de corretores), idEquipe guardado para enviar no filtro da API
  const equipesOptions: Option[] = isDiretor
    ? dataEquipesPorSuperintendente?.data?.map((e: any) => ({
        value: e.idUsuario, // Mantém idUsuario para a API
        label: e.apelido || e.nome || "",
        idEquipeLiderUsuario: e.idEquipeLiderUsuario, // Adiciona idEquipeLiderUsuario para o filtro
      })) || []
    : dataEquipes?.data?.map((e: any) => ({
        value: e.idUsuario, // Mantém idUsuario para a API
        label: e.apelido || e.nome || "",
        idEquipeLiderUsuario: e.idEquipeLiderUsuario, // Adiciona idEquipeLiderUsuario para o filtro
      })) || [];

  // Superintendentes para diretor
  // value = idUsuario (para cascata de busca de equipes), idEquipe guardado para enviar no filtro da API
  const superintendentesOptions: Option[] =
    dataSuperintendentes?.data?.map((s: any) => ({
      value: s.idUsuario,
      label: s.apelido || s.nome || "",
      idEquipe: s.idEquipe,
    })) || [];

  return (
    <S.Content>
      <S.Row style={{ justifyContent: "space-between" }}>
        <IconsSVG.FilterModalIcon />
        <S.TextFilter>Filtros</S.TextFilter>
        <S.ButtonClear onClick={handleClearFilters}>Limpar</S.ButtonClear>
      </S.Row>
      <S.SpaceY space={32} />

      {/* Data Visita */}
      <S.Row>
        <DatePickerRangeG
          startDate={dataVisitaInicio}
          endDate={dataVisitaFim}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
          disabled={false}
        />
      </S.Row>
      <S.SpaceY space={16} />

      {/* Origem */}
      <S.LabelText>Origem</S.LabelText>
      <S.Autocomplete
        fullWidth
        multiple
        id="filtro-origem"
        options={origensList}
        size="small"
        disableCloseOnSelect
        getOptionLabel={(option: any) => option.label || ""}
        // @ts-ignore
        getOptionSelected={(option: any, value: any) =>
          option.value === value.value
        }
        value={origem}
        onChange={(_event: any, value: any) => setOrigem(value || [])}
        renderInput={(params) => (
          <S.TextField {...params} placeholder="" variant="outlined" />
        )}
      />
      <S.SpaceY space={16} />

      {/* Canal de Contato */}
      <S.LabelText>Canal de Contato</S.LabelText>
      {loadingCanais ? (
        <Skeleton variant="rounded" width="100%" height={40} />
      ) : (
        <S.Autocomplete
          fullWidth
          multiple
          id="filtro-canal"
          options={canaisContatoList}
          size="small"
          disableCloseOnSelect
          disabled={origem.length === 0}
          getOptionLabel={(option: any) => option.label || ""}
          // @ts-ignore
          getOptionSelected={(option: any, value: any) =>
            option.value === value.value
          }
          value={canalContato}
          onChange={(_event: any, value: any) => setCanalContato(value || [])}
          renderInput={(params) => (
            <S.TextField {...params} placeholder="" variant="outlined" />
          )}
        />
      )}
      <S.SpaceY space={16} />

      {/* Tipo de Atendimento */}
      <S.LabelText>Tipo de Atendimento</S.LabelText>
      <S.Autocomplete
        fullWidth
        multiple
        id="filtro-tipo-atendimento"
        options={tiposAtendimentoList}
        size="small"
        disableCloseOnSelect
        getOptionLabel={(option: any) => option.label || ""}
        // @ts-ignore
        getOptionSelected={(option: any, value: any) =>
          option.value === value.value
        }
        value={tipoAtendimento}
        onChange={(_event: any, value: any) => setTipoAtendimento(value || [])}
        renderInput={(params) => (
          <S.TextField {...params} placeholder="" variant="outlined" />
        )}
      />
      <S.SpaceY space={16} />

      {/* Empreendimento */}
      <S.LabelText>Empreendimento</S.LabelText>
      {loadingEmpreendimentos ? (
        <Skeleton variant="rounded" width="100%" height={40} />
      ) : (
        <S.Autocomplete
          fullWidth
          multiple
          id="filtro-empreendimento"
          options={empreendimentosList}
          size="small"
          disableCloseOnSelect
          getOptionLabel={(option: any) => option.label || ""}
          // @ts-ignore
          getOptionSelected={(option: any, value: any) =>
            option.value === value.value
          }
          value={empreendimento}
          onChange={(_event: any, value: any) => setEmpreendimento(value || [])}
          renderInput={(params) => (
            <S.TextField {...params} placeholder="" variant="outlined" />
          )}
        />
      )}
      <S.SpaceY space={16} />

      {/* Superintendente - Apenas Diretor */}
      {isDiretor && (
        <>
          <S.LabelText>Superintendente</S.LabelText>
          <S.Autocomplete
            fullWidth
            multiple
            id="filtro-superintendente"
            options={superintendentesOptions}
            size="small"
            getOptionLabel={(option: any) => option.label || ""}
            // @ts-ignore
            getOptionSelected={(option: any, value: any) =>
              option.value === value.value
            }
            value={superintendente}
            onChange={(_event: any, value: any) => {
              // Limita a 1 seleção (mantém componente multiple para possível uso futuro)
              const selected = value || [];
              setSuperintendente(
                selected.length > 1 ? [selected[selected.length - 1]] : selected
              );
            }}
            renderInput={(params) => (
              <S.TextField {...params} placeholder="" variant="outlined" />
            )}
          />
          <S.SpaceY space={16} />
        </>
      )}

      {/* Equipe - Superintendente e Diretor */}
      {(isSuperintendente || isDiretor) && (
        <>
          <S.LabelText>Equipe</S.LabelText>
          <S.Autocomplete
            fullWidth
            multiple
            id="filtro-equipe"
            options={equipesOptions}
            size="small"
            disableCloseOnSelect
            disabled={isDiretor && superintendente.length === 0}
            getOptionLabel={(option: any) => option.label || ""}
            // @ts-ignore
            getOptionSelected={(option: any, value: any) =>
              option.idEquipeLiderUsuario === value.idEquipeLiderUsuario
            }
            value={equipe}
            onChange={(_event: any, value: any) => setEquipe(value || [])}
            renderInput={(params) => (
              <S.TextField {...params} placeholder="" variant="outlined" />
            )}
          />
          <S.SpaceY space={16} />
        </>
      )}

      {/* Corretor de Origem - Gerente, Superintendente e Diretor */}
      {(isGerente || isSuperintendente || isDiretor) && (
        <>
          <S.LabelText>Corretor de Origem</S.LabelText>
          <S.Autocomplete
            fullWidth
            multiple
            id="filtro-corretor-origem"
            options={corretoresOrigemOptions}
            size="small"
            disableCloseOnSelect
            disabled={(isSuperintendente || isDiretor) && equipe.length === 0}
            getOptionLabel={(option: any) => option.label || ""}
            // @ts-ignore
            getOptionSelected={(option: any, value: any) =>
              option.value === value.value
            }
            value={corretorOrigem}
            onChange={(_event: any, value: any) =>
              setCorretorOrigem(value || [])
            }
            renderInput={(params) => (
              <S.TextField {...params} placeholder="" variant="outlined" />
            )}
          />
          <S.SpaceY space={16} />
        </>
      )}

      {/* Corretor de Atendimento - Oculto para Corretor, Gerente, Superintendente e Diretor */}
      {!isCorretor && !isGerente && !isSuperintendente && !isDiretor && (
        <>
          <S.LabelText>Corretor de Atendimento</S.LabelText>
          <S.Autocomplete
            fullWidth
            multiple
            id="filtro-corretor-atendimento"
            options={corretoresOptions}
            size="small"
            disableCloseOnSelect
            getOptionLabel={(option: any) => option.label || ""}
            // @ts-ignore
            getOptionSelected={(option: any, value: any) =>
              option.value === value.value
            }
            value={corretorAtendimento}
            onChange={(_event: any, value: any) =>
              setCorretorAtendimento(value || [])
            }
            renderInput={(params) => (
              <S.TextField {...params} placeholder="" variant="outlined" />
            )}
          />
        </>
      )}
      <S.SpaceY space={32} />

      {/* Botão Aplicar Filtro */}
      <S.Row>
        <S.ButtonFilter onClick={handleApplyFilters}>
          Aplicar Filtro
        </S.ButtonFilter>
      </S.Row>
    </S.Content>
  );
};

export default FiltroVisitas;
